import { AnyAction } from '@reduxjs/toolkit';

import * as pharmacistThunks from '../actions/pharmacistActions';
import * as pharmacyThunks from '../actions/pharmacyActions';
import * as professionalThunks from '../actions/professionalActions';
import {
  AnalyticsShiftType,
  FeatureEvent,
  GA_EVENTS,
} from '../models/AnalyticsModel';
import { IRequestBookingParams } from '../models/PharmacistModels';
import { recurrenceType as RecurrenceTypes } from '../helpers/Constants';

export type FeatureEventRule = (action: AnyAction) => FeatureEvent | null;

const createRule =
  <TAction extends AnyAction>(
    matcher: (action: AnyAction) => action is TAction,
    build: (action: TAction) => FeatureEvent,
  ): FeatureEventRule =>
  (action) => {
    if (!matcher(action)) {
      return null;
    }

    return build(action);
  };

const isRecurring = (recurrence: number) =>
  recurrence !== RecurrenceTypes.Never;

// THE THUNK ALSO ACCEPTS A BARE WORKSHIFT ID, SO THE ARG IS NARROWED FIRST
const getRequestShiftType = (params: IRequestBookingParams | string) => {
  const hasCounterOffer = typeof params === 'object' && !!params.counterOffer;
  if (hasCounterOffer) {
    return AnalyticsShiftType.counterOffer;
  }

  return AnalyticsShiftType.shortTerm;
};

const { requestBooking, requestContract, createAvailability } =
  pharmacistThunks;

const { pharmacyAddFavourite, pharmacyRemoveFavourite } = pharmacyThunks;

const { generateProfessionalInvoice } = professionalThunks;

// ONLY THESE FEATURES ARE TRACKED. FIRST MATCH WINS
export const FEATURE_EVENT_RULES: Array<FeatureEventRule> = [
  // THE MAP AND THE CALENDAR BOTH REQUEST A SHIFT THROUGH THESE THUNKS
  createRule(requestBooking.fulfilled.match, (action) => ({
    event: GA_EVENTS.shiftRequestSubmitted,
    shift_type: getRequestShiftType(action.meta.arg),
  })),
  createRule(requestContract.fulfilled.match, () => ({
    event: GA_EVENTS.shiftRequestSubmitted,
    shift_type: AnalyticsShiftType.longTerm,
  })),
  // preSubmitAvailabilities IS ONLY A CONFLICT CHECK BEFORE THIS CALL
  createRule(createAvailability.fulfilled.match, (action) => ({
    event: GA_EVENTS.availabilityPublished,
    is_recurring: isRecurring(action.meta.arg.recurrenceType),
  })),
  createRule(generateProfessionalInvoice.fulfilled.match, (action) => ({
    event: GA_EVENTS.invoiceGenerated,
    count: action.meta.arg.bookings.length,
  })),
  createRule(pharmacyAddFavourite.fulfilled.match, () => ({
    event: GA_EVENTS.replacementLiked,
    is_liked: true,
  })),
  createRule(pharmacyRemoveFavourite.fulfilled.match, () => ({
    event: GA_EVENTS.replacementLiked,
    is_liked: false,
  })),
];

export const findFeatureEvent = (action: AnyAction): FeatureEvent | null =>
  FEATURE_EVENT_RULES.reduce<FeatureEvent | null>(
    (event, rule) => event ?? rule(action),
    null,
  );
